import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  getAffiliateBySlug,
  markSupportGroupJoined,
  OFFICIAL_USER_SUPPORT_GROUP
} from '../services/affiliateService';
import { Loader2, MessageCircle, CheckCircle2, Users, ExternalLink } from 'lucide-react';

/**
 * /r/support — shown once after a fresh trial-claim.
 * Prefer referring agent's supportGroupLink; fall back to the master SESAAT
 * community group when no link is set (or no agent at all).
 */
export default function SupportGroupView() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading' | 'show'
  const [affiliate, setAffiliate] = useState(null);
  const [groupLink, setGroupLink] = useState(OFFICIAL_USER_SUPPORT_GROUP);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const user = auth.currentUser;
      if (!user) { navigate('/', { replace: true }); return; }

      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const refCode = userSnap.exists() ? userSnap.data()?.referredBy : null;
        if (refCode) {
          const aff = await getAffiliateBySlug(refCode);
          if (!cancelled && aff) {
            setAffiliate(aff);
            if (aff.supportGroupLink) setGroupLink(aff.supportGroupLink);
          }
        }
      } catch (_) {}
      if (!cancelled) setStatus('show');
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const handleJoinClick = () => {
    window.open(groupLink, '_blank', 'noopener,noreferrer');
  };

  const handleAlreadyJoined = async () => {
    setJoining(true);
    try {
      await markSupportGroupJoined();
    } catch (_) {}
    navigate('/', { replace: true });
  };

  const handleSkip = () => {
    navigate('/', { replace: true });
  };

  if (status !== 'show') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  const usingAgentLink = !!affiliate?.supportGroupLink;
  const ejenName = affiliate?.name || 'ejen anda';

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 bg-gradient-to-br from-emerald-50 via-white to-amber-50 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900">
      <div className="w-full max-w-md">
        <div className="relative bg-white dark:bg-slate-900 rounded-3xl border-2 border-emerald-200 dark:border-emerald-800/40 shadow-xl shadow-emerald-500/10 p-6 sm:p-8">
          <div className="flex justify-center mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <Users className="w-7 h-7 text-white" />
            </div>
          </div>

          <h1 className="text-center text-xl sm:text-2xl font-extrabold text-slate-900 dark:text-white mb-2">
            Sertai Komuniti Sokongan
          </h1>
          <p className="text-center text-sm text-slate-600 dark:text-slate-400 mb-5 leading-relaxed">
            {usingAgentLink ? (
              <>Ejen anda <strong className="text-slate-900 dark:text-white">{ejenName}</strong> ada
              group WhatsApp khas untuk newbie SESAAT. Sertai untuk dapat bantuan terus & tips harian.</>
            ) : (
              <>Sertai <strong className="text-slate-900 dark:text-white">komuniti rasmi SESAAT</strong> untuk
              dapat bantuan terus daripada team support & tips harian dari pengguna lain.</>
            )}
          </p>

          <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40 p-4 mb-5">
            <div className="flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <ul className="text-xs sm:text-sm text-emerald-800 dark:text-emerald-300 leading-relaxed space-y-1">
                <li>Tanya soalan terus pada ejen + member</li>
                <li>Update terkini tools & teknik baharu</li>
                <li>Komuniti pengguna SESAAT yang aktif</li>
              </ul>
            </div>
          </div>

          <button
            type="button"
            onClick={handleJoinClick}
            className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-extrabold text-sm shadow-lg shadow-emerald-500/30 active:scale-[0.98] transition-all"
          >
            <MessageCircle className="w-4 h-4 fill-current" />
            Sertai Group WhatsApp
            <ExternalLink className="w-3.5 h-3.5 opacity-80" />
          </button>

          <button
            type="button"
            onClick={handleAlreadyJoined}
            disabled={joining}
            className="w-full mt-3 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold text-sm active:scale-[0.98] transition-all disabled:opacity-50"
          >
            {joining ? <><Loader2 className="w-4 h-4 animate-spin" /> Memuatkan...</> : 'Saya Dah Join'}
          </button>

          <button
            type="button"
            onClick={handleSkip}
            disabled={joining}
            className="w-full mt-2 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 font-medium py-2 transition-colors disabled:opacity-50"
          >
            Skip dulu, terus ke dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
